import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  LogRepository,
  Order,
  OrderRepository,
  OrderStatus,
  Shops,
  UserAddress,
  Where
} from '@infrab4a/connect';
import { getNationalHolidaysByYear } from 'brazil-holidays';
import { lastValueFrom, map, retry } from 'rxjs';
import { UserControllerService } from 'src/app/admin-api';
import { ConnectRequest } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { environment } from 'src/environments/environment';
import { getShop } from '../../enums/ShopMap';
import { ShippingCompanyUpdateRequest } from '../../models/ShippingCompanyUpdateRequest';
import { BaseConnectService } from './base-connect.service';

export interface ShopOrderRefundResponse {
  orderId?: string;
  orderNumber?: string;
  refund?: boolean | null;
  refundStatus?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ShopOrderService extends BaseConnectService<Order> {
  holidays: { date: string; name: string; type: string }[];

  constructor(
    @Inject('OrderRepository')
    private orderRepository: OrderRepository,
    @Inject('LogRepository')
    private logRepository: LogRepository,
    private userService: UserControllerService,
    private httpClient: HttpClient
  ) {
    super(orderRepository);
    getNationalHolidaysByYear(new Date().getFullYear())
      .then((holidays) => (this.holidays = holidays))
      .catch((error) => AppDialogService.showErrorDialog(error));
  }

  async getOrderById(id: string): Promise<Order> {
    return await this.orderRepository.get({ id });
  }

  async getOrderByOrderNumber(orderNumber: string): Promise<Order> {
    const result = await this.orderRepository.find({
      filters: { orderNumber }
    });
    return result?.data?.at(0);
  }

  async getOrdersByEmail(email: string): Promise<Order[]> {
    return this.orderRepository
      .find({
        filters: {
          user: {
            email
          }
        }
      })
      .then((res) => res.data);
  }

  async getOrdersByPersonId(id: number): Promise<Order[]> {
    return this.orderRepository
      .find({
        filters: {
          user: {
            id: id.toString()
          }
        }
      })
      .then((res) => res.data);
  }

  async updateOrderStatus(
    status: OrderStatus,
    id: string,
    removePedidov = false
  ) {
    const partial = { id, status };
    const log = await this.getLogInfo();

    if (removePedidov) partial['pedidov'] = null;
    return this.orderRepository.update(
      new Order({
        ...partial,
        updatedBy: log,
        updatedAt: new Date().getTime()
      } as any)
    );
  }

  async updateShippingCompany(
    id: string,
    request: ShippingCompanyUpdateRequest
  ) {
    const log = await this.getLogInfo();

    return this.orderRepository.update(
      new Order({
        id,
        shipping: {
          ShippingServiceId: request.shippingServiceId,
          totalPrice: 0.0,
          serviceCode: request.shippingServiceId,
          daysToDelivery: 0,
          additionalDescription: request.description,
          description: request.description,
          cnpj: request.cnpj,
          serviceName: request.serviceName,
          ShippingCompanyId: request.shippingCompanyId,
          ShippingCompanyName: request.ShippingCompanyName
        },
        updatedBy: log,
        updatedAt: new Date().getTime()
      } as any)
    );
  }

  async getOrders(dateInit: Date, shop?: string): Promise<Array<Order>> {
    const filters = {
      createdAt: {
        operator: Where.GTE,
        value: dateInit.getTime()
      }
      // status: {
      //   operator: Where.EQUALS,
      //   value: 'Entregue'
      // },
      // 'shipping.ShippingCompanyName': {
      //   operator: Where.EQUALS,
      //   value: 'Glam'
      // }
    };
    if (shop) {
      filters['shop'] = {
        operator: Where.EQUALS,
        value: getShop(shop)
      };
    }
    return (
      await this.orderRepository.find({
        filters
      } as any)
    ).data.map((o) => {
      o.user.displayName =
        o.user?.firstName || o.user.lastName
          ? o.user.firstName + ' ' + o.user.lastName
          : o.user.displayName;
      return o;
    });
  }

  async paidToday() {
    const date = new Date();
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    const orders = await this.getOrdersList({
      filters: [
        {
          condition: 'gte',
          field: 'createdAt',
          fieldType: 'date',
          value: date
        },
        {
          condition: 'equals',
          field: 'payment.status',
          fieldType: 'text',
          value: 'paid'
        }
      ],
      sortBy: 'createdAt',
      sortDirection: 'desc',
      page: 0,
      pageSize: 9999
    });
    return orders.content.filter(
      (o) =>
        o.status !== OrderStatus.CANCELADO &&
        [Shops.GLAMSHOP, Shops.MENSMARKET].includes(o.shop)
    );
  }

  async updateOrderAddress(
    address: Partial<UserAddress>,
    type: 'billing' | 'shipping' = 'billing',
    id: string
  ) {
    const body = { id };
    body[type + 'Address'] = {
      ...address,
      mailbox: address.mailboxNumber?.length > 0
    } as UserAddress;
    const log = await this.getLogInfo();

    return this.orderRepository.update(
      new Order({
        ...body,
        updatedBy: log,
        updatedAt: new Date().getTime()
      } as any)
    );
  }

  async getOrdersList(request: Partial<ConnectRequest>) {
    const result = await this.paginatedSearch(request);
    const content = result.data.map((category) => ({ ...category }) as Order);
    const totalPages = request.pageSize
      ? Math.floor(result.count / request.pageSize)
      : undefined;
    const totalElements = result.count;

    return {
      totalPages,
      totalElements,
      content,
      empty: result.count === 0
    };
  }

  deliveryDate(order: Partial<Order>): string {
    const paidAt = order.payment?.paid_at || order.payment?.['paidAt'];
    if (!paidAt) {
      return '';
    }
    if (!order.shipping?.DaysToDelivery) {
      return (
        (order.shipping as any)?.additionalDescription ||
        (order.shipping as any)?.description ||
        'Indeterminado'
      );
    }
    const datePipe = new DatePipe('pt-BR');
    const date = new Date(paidAt);
    for (let index = 0; index < order.shipping.DaysToDelivery; index++) {
      date.setDate(date.getDate() + 1);
      while (
        date.getDay() === 0 ||
        date.getDay() === 6 ||
        this.holidays?.some(
          (h) => h.date === datePipe.transform(date, 'yyyy-MM-dd')
        )
      ) {
        date.setDate(date.getDate() + 1);
      }
    }
    return datePipe.transform(date, 'dd/MM/yyyy');
  }

  async getLogInfo() {
    const user = await lastValueFrom(
      this.userService.getInfo().pipe(map((data) => data.result))
    );

    const log = {
      id: user.userId.toString(),
      name: user.displayName,
      email: user.username,
      origin: 'ADMIN',
      updatedAt: new Date()
    };

    return log;
  }

  async getOrderLogs(orderId: string) {
    const { data } = await this.logRepository.find({
      filters: {
        document: {
          id: {
            operator: Where.EQUALS,
            value: orderId
          }
        }
      },
      orderBy: {
        date: 'asc'
      }
    });

    return data;
  }

  approveOrDisapproveReshipment(orderNumber: string, approved: boolean = true) {
    return this.httpClient
      .post(
        `https://southamerica-east1-${environment.connectFirebase.projectId}.cloudfunctions.net/approveOrDisproveOrderReshipment`,
        { orderNumber, approved }
      )
      .pipe(retry(1));
  }

  revalidateReshipmentStock(orderNumber: string) {
    return this.httpClient
      .post(
        `https://southamerica-east1-${environment.connectFirebase.projectId}.cloudfunctions.net/revalidateStockOrderReshipment`,
        { orderNumber }
      )
      .pipe(retry(1));
  }

  refundOrder(
    orderNumber: string,
    items?: string[],
    fullRefund?: boolean,
    motivo?: string,
    user?: {
      id: string;
      name: string;
      email: string;
      origin?: string;
    },
    shouldRefund?: boolean,
    shouldResend?: boolean
  ) {
    return this.httpClient
      .post<ShopOrderRefundResponse>(
        `https://southamerica-east1-${environment.connectFirebase.projectId}.cloudfunctions.net/createOrderRefundWithReshipment`,
        { orderNumber, items, fullRefund, motivo, user, shouldRefund, shouldResend }
      )
      .pipe(retry(1));
  }
}
